import { Component, ReactNode } from "react";

import { safeSessionStorage } from "~/shared/utils/safeStorage";

const RELOAD_FLAG_KEY = "chunk-load-error-reloaded";

const CHUNK_ERROR_PATTERNS = [
  /Loading chunk [\d]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /Unable to preload CSS/i,
];

const isChunkLoadError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  if (error.name === "ChunkLoadError") return true;
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(error.message));
};

interface ChunkLoadErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

interface ChunkLoadErrorBoundaryState {
  hasUnrecoverableError: boolean;
}

export class ChunkLoadErrorBoundary extends Component<
  ChunkLoadErrorBoundaryProps,
  ChunkLoadErrorBoundaryState
> {
  state: ChunkLoadErrorBoundaryState = { hasUnrecoverableError: false };

  static getDerivedStateFromError(error: unknown): ChunkLoadErrorBoundaryState {
    if (!isChunkLoadError(error)) throw error;

    if (safeSessionStorage.getItem(RELOAD_FLAG_KEY)) {
      return { hasUnrecoverableError: true };
    }

    // First chunk load failure in this tab session: reload once so the browser
    // picks up the new asset manifest. The flag persists for the tab lifetime
    // so a second failure falls through to the fallback UI instead of looping.
    safeSessionStorage.setItem(RELOAD_FLAG_KEY, "1");
    window.location.reload();
    return { hasUnrecoverableError: false };
  }

  render(): ReactNode {
    if (this.state.hasUnrecoverableError) return this.props.fallback;
    return this.props.children;
  }
}
